import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { parseActiveStatusFilter, toActiveStatusFilterText } from '@/utils/filters';
import type { MarketProjectSearch } from '../types/marketPromotionP2.types';

const route = getRouteApi('/_auth/market-promotion/projects');

type FilterValues = Record<string, string>;

const toFilterValues = (search: Pick<MarketProjectSearch, 'isActive'>): FilterValues => ({
  isActive: toActiveStatusFilterText(search.isActive),
});

const toFilterSearch = (values: FilterValues): Pick<MarketProjectSearch, 'isActive'> => ({
  isActive: parseActiveStatusFilter(values.isActive),
});

const normalizeFilterValues = (values: FilterValues) => toFilterValues(toFilterSearch(values));

export function useMarketProjectFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedValues = useMemo(() => toFilterValues(search), [search]);

  const filters = useUrlSyncedFilters({
    appliedValues,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const nameSearch = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, search: text }),
        replace: true,
      });
    },
  });

  return { ...filters, ...nameSearch, search };
}
